/**
 * Machine dock ops — network · power · GPIO · Wi‑Fi · reboot.
 * TF card lives on the Hardware facts plane (format + meter only).
 * Soft≠Live: Live fields only when reported; localhost drafts never invent as Live.
 * Save = client format guards + local honesty when Live push plane is off.
 */
import { useEffect, useMemo, useState } from "react";
import { html, Ico } from "./ui.js";
import { path, useApi, useAction } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { boardTelemetryViewOf, pinWakeOf } from "./edgeos-live.js";
import { poeLiveFromTelemetry } from "./board-telemetry-adapter.js";
import { confirmDeviceCommand } from "./device-command-gate.js";
import {
  useWifiOpsStatus,
  wifiOpsIdempotencyKey,
  wifiOpsPath,
  wifiOpsPollJob,
  wifiOpsStatusViewOf,
  wifiSwitchedSay,
} from "./machine-wifi-ops.js";
import { softNetGet, softNetPut } from "./machine-soft-net-store.js";
import { isLocalHost } from "./pathseal.js";
import { FactRow } from "./machine-estate-controls.js";

const IPV4_RE = /^(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)$/;
/** Live API: delay_s 5..604800 · at_ts absolute. Weekly = next one-shot at_ts (no recurring cron on this path). */
const REBOOT_MODES = [
  { id: "delay", label: "In…", say: "Relative delay" },
  { id: "at", label: "At time", say: "One specific moment" },
  { id: "weekly", label: "Weekly", say: "Next weekday slot" },
];
const REBOOT_DELAYS = [
  { id: 15, label: "15 s", say: "Brief" },
  { id: 60, label: "1 min", say: "Short" },
  { id: 300, label: "5 min", say: "Grace" },
  { id: 900, label: "15 min", say: "Hold" },
  { id: 3600, label: "1 h", say: "Hour" },
  { id: 21600, label: "6 h", say: "Shift" },
  { id: 86400, label: "1 day", say: "Day" },
  { id: 259200, label: "3 days", say: "Far" },
  { id: 604800, label: "7 days", say: "Max" },
];
const REBOOT_WEEKDAYS = [
  { id: 1, label: "Mon" },
  { id: 2, label: "Tue" },
  { id: 3, label: "Wed" },
  { id: 4, label: "Thu" },
  { id: 5, label: "Fri" },
  { id: 6, label: "Sat" },
  { id: 0, label: "Sun" },
];

function pad2(n) {
  return String(n).padStart(2, "0");
}

function defaultAtLocalValue(offsetMin = 60) {
  const d = new Date(Date.now() + offsetMin * 60 * 1000);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function atTsFromLocalInput(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const ms = Date.parse(raw);
  if (!Number.isFinite(ms)) return null;
  return Math.floor(ms / 1000);
}

/** Next weekly slot as unix seconds. dow matches Date#getDay (0=Sun). */
function nextWeeklyAtTs(dow, hour, minute) {
  const day = ((Number(dow) % 7) + 7) % 7;
  const hh = Math.max(0, Math.min(23, Number(hour) || 0));
  const mm = Math.max(0, Math.min(59, Number(minute) || 0));
  const now = new Date();
  const target = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hh, mm, 0, 0);
  let add = (day - now.getDay() + 7) % 7;
  if (add === 0 && target.getTime() <= now.getTime() + 5000) add = 7;
  target.setDate(target.getDate() + add);
  return Math.floor(target.getTime() / 1000);
}

function fmtLocalFromTs(ts) {
  const n = Number(ts);
  if (!Number.isFinite(n) || n <= 0) return "—";
  const d = new Date(n * 1000);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function textOf(...values) {
  for (const value of values) {
    const text = String(value == null ? "" : value).trim();
    if (text) return text;
  }
  return "";
}

function objectOf(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function fmtTemp(n) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return Number(n).toFixed(1) + " °C";
}

function fmtWatts(n) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return Number(n).toFixed(1) + " W";
}

function safePath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    const id = encodeURIComponent(String((opts && opts.pathParams && opts.pathParams[0]) || ""));
    if (!id) return "";
    if (op === "get__devices_p_board-telemetry") return "/devices/" + id + "/board-telemetry";
    if (op === "get__devices_p_pins") return "/devices/" + id + "/pins";
    if (op === "post__devices_p_schedule-reboot") return "/devices/" + id + "/schedule-reboot";
    return "";
  }
}

function ipv4OrEmpty(value, label) {
  const t = String(value || "").trim();
  if (!t) return { ok: true, value: "" };
  if (!IPV4_RE.test(t)) return { ok: false, error: label + " must be a valid IPv4 address" };
  return { ok: true, value: t };
}

export default function MachineInspectOps({ deviceId, detail, device, toast, facet = "net", flat = false }) {
  const local = isLocalHost();
  const did = String(deviceId || "").trim();
  const act = useAction();
  const posture = useApi(did ? path("get__fleet_device_status") : "");
  const cmdOk = posture.loading ? false : (
    !!(posture.data && posture.data.commands_allowed === true)
  );
  const wifiOpsApi = useWifiOpsStatus(local ? "" : did);
  const wifiOpsView = useMemo(() => wifiOpsStatusViewOf(wifiOpsApi.data), [wifiOpsApi.data]);
  const teleApi = useApi(did ? safePath("get__devices_p_board-telemetry", { pathParams: [did] }) : "");
  const pinsApi = useApi(did ? safePath("get__devices_p_pins", { pathParams: [did] }) : "");

  const tele = useMemo(() => boardTelemetryViewOf(teleApi.data), [teleApi.data]);
  const poe = useMemo(() => poeLiveFromTelemetry(teleApi.data), [teleApi.data]);
  const seats = useMemo(() => {
    const raw = (pinsApi.data && (pinsApi.data.items || pinsApi.data.pins || pinsApi.data.seats)) || [];
    return Array.isArray(raw) ? raw : [];
  }, [pinsApi.data]);

  const d = objectOf(detail);
  const net = objectOf(d.net_health);
  const gwObs = objectOf(d.gateway_observation);
  const liveIp = textOf(d.ip, d.ip_address, d.lan_ip, d.wifi_ip, net.ip, net.ip_address, gwObs.ip);
  const liveGw = textOf(d.gateway, d.gw, net.gateway, net.gw, gwObs.gateway, gwObs.gw);
  const liveMask = textOf(d.subnet, d.subnet_mask, d.netmask, net.subnet, net.netmask, gwObs.subnet, gwObs.netmask);

  const soft = local
    ? (softNetGet(did) || { ip: "", gateway: "", subnet: "", ssid: "", pskQueued: false })
    : { ip: "", gateway: "", subnet: "", ssid: "", pskQueued: false };
  const [ip, setIp] = useState(soft.ip || liveIp);
  const [gateway, setGateway] = useState(soft.gateway || liveGw);
  const [subnet, setSubnet] = useState(soft.subnet || liveMask);
  const [ssid, setSsid] = useState(soft.ssid || textOf(tele && tele.net && tele.net.ssid));
  const [psk, setPsk] = useState("");
  const [netDirty, setNetDirty] = useState(false);
  const [wifiDirty, setWifiDirty] = useState(false);
  const [openLane, setOpenLane] = useState("");
  const [rebootOpen, setRebootOpen] = useState(false);
  const [rebootMode, setRebootMode] = useState("delay");
  const [rebootDelay, setRebootDelay] = useState(900);
  const [rebootAtLocal, setRebootAtLocal] = useState(() => defaultAtLocalValue(60));
  const [rebootWeekDay, setRebootWeekDay] = useState(1);
  const [rebootWeekTime, setRebootWeekTime] = useState("03:00");
  const [rebootStatus, setRebootStatus] = useState(null);

  useEffect(() => {
    if (facet === "board") setOpenLane("");
  }, [facet, did]);

  useEffect(() => {
    const s = local
      ? (softNetGet(did) || { ip: "", gateway: "", subnet: "", ssid: "" })
      : { ip: "", gateway: "", subnet: "", ssid: "" };
    setIp(s.ip || liveIp);
    setGateway(s.gateway || liveGw);
    setSubnet(s.subnet || liveMask);
    setSsid(s.ssid || textOf(tele && tele.net && tele.net.ssid));
    setPsk("");
    setNetDirty(false);
    setWifiDirty(false);
    setOpenLane(facet === "board" ? "" : "lan");
    setRebootOpen(false);
    setRebootMode("delay");
    setRebootDelay(900);
    setRebootAtLocal(defaultAtLocalValue(60));
    setRebootWeekDay(1);
    setRebootWeekTime("03:00");
    setRebootStatus(null);
  }, [did, liveIp, liveGw, liveMask, tele && tele.net && tele.net.ssid, facet]);

  const gpioRows = useMemo(() => seats.slice(0, 24).map((s, i) => {
    const role = textOf(s.role);
    const wake = pinWakeOf(s);
    const st = textOf(s.state || s.binding_state).toLowerCase();
    const used = !!(role || st === "claimed" || st === "verified" || st === "confirmed");
    return {
      key: String(s.seat_id || s.id || i),
      label: textOf(s.seat_id, s.id) || ("seat " + (i + 1)),
      gpio: s.gpio != null ? String(s.gpio) : "—",
      role: role || (used ? st || "used" : "free"),
      wake,
      used,
    };
  }), [seats]);

  const chipTemp = tele && tele.temp != null && Number.isFinite(Number(tele.temp))
    ? Number(tele.temp) : null;
  const poeWatts = poe && poe.fields && poe.fields.watts;
  const poeVin = poe && poe.fields && poe.fields.vin;

  const saveNetDraft = () => {
    if (!did) return;
    const a = ipv4OrEmpty(ip, "Board IP");
    const b = ipv4OrEmpty(gateway, "Gateway");
    const c = ipv4OrEmpty(subnet, "Subnet mask");
    if (!a.ok) { toast && toast(a.error, "bad"); return; }
    if (!b.ok) { toast && toast(b.error, "bad"); return; }
    if (!c.ok) { toast && toast(c.error, "bad"); return; }
    if (!a.value && !b.value && !c.value) {
      toast && toast("Enter at least one LAN field before save", "warn");
      return;
    }
    if (!local) {
      toast && toast("LAN push refused · Live net plane not armed", "bad");
      return;
    }
    softNetPut(did, { ip: a.value, gateway: b.value, subnet: c.value });
    setIp(a.value);
    setGateway(b.value);
    setSubnet(c.value);
    setNetDirty(false);
    toast && toast(
      local
        ? "LAN draft saved locally · IP push not available here"
        : "LAN draft queued locally · not applied to device",
      "warn",
    );
  };

  const queueWifi = async () => {
    if (!did) return;
    const name = String(ssid || "").trim();
    if (!name) {
      toast && toast("SSID is empty · nothing to save", "bad");
      return;
    }
    if (name.length > 32) {
      toast && toast("SSID must be 32 characters or fewer", "bad");
      return;
    }
    if (!String(psk || "").trim()) {
      toast && toast("Enter a passphrase to save", "bad");
      return;
    }
    if (local) {
      softNetPut(did, { ssid: name, pskQueued: true, medium: "wifi" });
      setPsk("");
      setWifiDirty(false);
      toast && toast("Wi‑Fi saved locally · not sent to board", "warn");
      return;
    }
    if (!cmdOk) {
      toast && toast("Commands closed for this seat", "warn");
      return;
    }
    const ok = await confirmDeviceCommand({
      kind: "wifi_config",
      targets: [{ id: did, label: textOf(device && device.label, did) }],
      detail: "Switch Wi-Fi on this board via Live wifi-ops job.",
      confirmLabel: "Switch Wi-Fi",
    });
    if (!ok) return;
    const pass = String(psk || "");
    await act.run(wifiOpsPath("post__devices_p_wifi-ops_stage", did), {
      method: "POST",
      body: { ssid: name },
    });
    const out = await act.run(wifiOpsPath("post__devices_p_wifi-ops_switch", did), {
      method: "POST",
      headers: { "Idempotency-Key": wifiOpsIdempotencyKey("switch", did) },
      body: { ssid: name, password: pass },
    });
    if (!out || !out.job) {
      toast && toast(act.error || "Switch refused", "bad");
      return;
    }
    setPsk("");
    setWifiDirty(false);
    const polled = await wifiOpsPollJob(act, did, out.job.job_id);
    wifiOpsApi.reload && wifiOpsApi.reload();
    const say = wifiSwitchedSay(wifiOpsView, polled);
    toast && toast(say.say, say.tone === "go" ? "go" : say.tone === "bad" ? "bad" : "warn");
  };

  const resolveRebootPlan = () => {
    const mode = REBOOT_MODES.some((m) => m.id === rebootMode) ? rebootMode : "delay";
    if (mode === "delay") {
      const delay = REBOOT_DELAYS.some((d) => d.id === rebootDelay)
        ? rebootDelay
        : Math.max(5, Math.min(604800, Number(rebootDelay) || 900));
      const chip = REBOOT_DELAYS.find((d) => d.id === delay);
      return {
        ok: true,
        mode,
        body: { delay_s: delay },
        whenSay: chip ? ("In " + chip.label) : ("In " + delay + "s"),
        factWhen: (chip ? chip.label : (delay + "s")) + " from now",
        confirmLabel: "Arm reboot · " + (chip ? chip.label : (delay + "s")),
      };
    }
    if (mode === "at") {
      const at = atTsFromLocalInput(rebootAtLocal);
      const now = Math.floor(Date.now() / 1000);
      if (at == null) return { ok: false, error: "Pick a valid date and time" };
      if (at <= now + 5) return { ok: false, error: "At-time must be more than 5 seconds ahead" };
      return {
        ok: true,
        mode,
        body: { at_ts: at },
        whenSay: "At " + fmtLocalFromTs(at),
        factWhen: fmtLocalFromTs(at),
        confirmLabel: "Arm reboot · at time",
      };
    }
    const parts = String(rebootWeekTime || "03:00").split(":");
    const hh = Number(parts[0]);
    const mm = Number(parts[1]);
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) {
      return { ok: false, error: "Weekly time must be HH:MM" };
    }
    const at = nextWeeklyAtTs(rebootWeekDay, hh, mm);
    const day = REBOOT_WEEKDAYS.find((d) => d.id === rebootWeekDay);
    const timeSay = pad2(hh) + ":" + pad2(mm);
    return {
      ok: true,
      mode: "weekly",
      body: { at_ts: at },
      whenSay: "Next " + ((day && day.label) || "day") + " " + timeSay,
      factWhen: ((day && day.label) || "day") + " " + timeSay + " · next " + fmtLocalFromTs(at),
      confirmLabel: "Arm next weekly slot",
      weeklyNote: "Live arms the next weekly slot only · not a recurring cron on this path.",
    };
  };

  const scheduleReboot = async () => {
    if (!did) return;
    if (!cmdOk) {
      toast && toast("Commands closed for this seat", "warn");
      return;
    }
    const plan = resolveRebootPlan();
    if (!plan.ok) {
      setRebootStatus({ tone: "bad", say: plan.error });
      toast && toast(plan.error, "bad");
      return;
    }
    const unitLabel = textOf(device && device.label, did);
    const liveSsid = textOf(ssid, tele && tele.net && tele.net.ssid, detail && detail.ssid);
    const presence = device && device.online ? "Online" : "Quiet";
    const linkSay = textOf(
      detail && detail.link_kind,
      detail && detail.net_health && detail.net_health.medium,
      tele && tele.net && tele.net.medium,
    ) || (liveSsid ? "Wi-Fi" : "—");
    const ok = await confirmDeviceCommand({
      kind: "schedule_reboot",
      targets: [{
        id: did,
        label: unitLabel,
        ssid: liveSsid || "",
        presence,
        link: linkSay,
      }],
      facts: [
        { k: "When", v: plan.factWhen, tone: "warn" },
        { k: "Mode", v: plan.mode === "weekly" ? "Weekly · next slot" : (plan.mode === "at" ? "Specific time" : "Relative delay") },
        { k: "SSID", v: liveSsid || "— not reported", readOnly: true },
        { k: "Presence", v: presence },
        { k: "Effect", v: "Restart firmware · not factory reset" },
      ],
      effects: [
        plan.mode === "weekly"
          ? "Arms the next weekday slot only · re-arm later for the week after (no Live recurring cron here)."
          : "Board restarts at the armed moment · radio / MQTT drop briefly.",
        "Presence returns when the unit boots and heartbeats again.",
        "Skills, config, and Wi-Fi credentials stay on the board (not wiped).",
        "Wrong unit = wrong site goes dark — match label, id, and SSID above.",
      ],
      detail: "Arm a scheduled restart on this claimed machine only. Local preview never invents a Live reboot.",
      confirmLabel: plan.confirmLabel,
      ackLabel: "I matched this unit’s name, id, SSID, and schedule — arm the restart.",
    });
    if (!ok) return;
    setRebootStatus({ tone: "send", say: "Arming reboot…" });
    const out = await act.run(
      safePath("post__devices_p_schedule-reboot", { pathParams: [did] }),
      { method: "POST", body: plan.body },
    );
    if (out) {
      const when = out.execute_at_ts ? fmtLocalFromTs(out.execute_at_ts) : plan.whenSay;
      const say = "Armed · " + when;
      setRebootStatus({ tone: "go", say });
      toast && toast("Reboot scheduled · " + when, "go");
      setRebootOpen(false);
      return;
    }
    const fail = local
      ? "Local preview · schedule-reboot not available here"
      : (act.error || "Schedule reboot refused");
    setRebootStatus({ tone: local ? "warn" : "bad", say: fail });
    toast && toast(fail, local ? "warn" : "bad");
  };

  const rebootPlanPreview = resolveRebootPlan();
  const rebootSummary = rebootStatus && rebootStatus.tone === "go"
    ? rebootStatus.say
    : (rebootPlanPreview.ok ? (rebootPlanPreview.whenSay + " · gated") : "Pick a schedule · gated");
  const rebootSsid = textOf(ssid, tele && tele.net && tele.net.ssid, detail && detail.ssid);

  const rebootBody = html`
    <div className="mh-reboot__body" id=${"mh-reboot-body-" + did}>
      <p className="mh-reboot__lead">
        Delayed firmware restart — not a wipe. Choose relative delay, a specific time, or the next weekly slot.
      </p>
      <dl className="mh-reboot__id" aria-label="Unit identity · read-only">
        <div data-ro="1">
          <dt>Unit</dt>
          <dd>${textOf(device && device.label, did) || "—"}</dd>
        </div>
        <div data-ro="1">
          <dt>Id</dt>
          <dd><code>${did || "—"}</code></dd>
        </div>
        <div data-ro="1" data-ssid="1">
          <dt>SSID</dt>
          <dd>${rebootSsid || "— not reported"}</dd>
        </div>
        <div data-ro="1">
          <dt>Presence</dt>
          <dd>${device && device.online ? "Online" : "Quiet"}</dd>
        </div>
      </dl>
      <div className="mh-reboot__modes" role="tablist" aria-label="Schedule mode">
        ${REBOOT_MODES.map((m) => html`
          <button key=${m.id} type="button" role="tab"
            className="mh-reboot__mode"
            aria-selected=${rebootMode === m.id ? "true" : "false"}
            data-on=${rebootMode === m.id ? "1" : "0"}
            disabled=${act.busy}
            onClick=${() => { setRebootMode(m.id); setRebootStatus(null); }}>
            <b>${m.label}</b>
            <small>${m.say}</small>
          </button>`)}
      </div>
      ${rebootMode === "delay" ? html`
        <div className="mh-reboot__delays" role="radiogroup" aria-label="Reboot delay">
          ${REBOOT_DELAYS.map((d) => html`
            <button key=${d.id} type="button" role="radio"
              className="mh-reboot__chip"
              aria-checked=${rebootDelay === d.id ? "true" : "false"}
              data-on=${rebootDelay === d.id ? "1" : "0"}
              disabled=${act.busy}
              onClick=${() => { setRebootDelay(d.id); setRebootStatus(null); }}>
              <b>${d.label}</b>
              <small>${d.say}</small>
            </button>`)}
        </div>` : null}
      ${rebootMode === "at" ? html`
        <label className="mh-reboot__field">
          <u>Specific time</u>
          <input className="inp" type="datetime-local"
            value=${rebootAtLocal}
            disabled=${act.busy}
            onChange=${(e) => { setRebootAtLocal(e.target.value); setRebootStatus(null); }}/>
          <small>Local clock · Live stores absolute at_ts</small>
        </label>` : null}
      ${rebootMode === "weekly" ? html`
        <div className="mh-reboot__weekly">
          <div className="mh-reboot__weekdays" role="radiogroup" aria-label="Weekday">
            ${REBOOT_WEEKDAYS.map((d) => html`
              <button key=${d.id} type="button" role="radio"
                className="mh-reboot__dow"
                aria-checked=${rebootWeekDay === d.id ? "true" : "false"}
                data-on=${rebootWeekDay === d.id ? "1" : "0"}
                disabled=${act.busy}
                onClick=${() => { setRebootWeekDay(d.id); setRebootStatus(null); }}>
                ${d.label}
              </button>`)}
          </div>
          <label className="mh-reboot__field">
            <u>Time each week</u>
            <input className="inp" type="time"
              value=${rebootWeekTime}
              disabled=${act.busy}
              onChange=${(e) => { setRebootWeekTime(e.target.value); setRebootStatus(null); }}/>
            <small data-tone="warn">Arms the next slot only · re-arm for the following week (API is one-shot).</small>
          </label>
        </div>` : null}
      <p className="mh-reboot__preview" data-ok=${rebootPlanPreview.ok ? "1" : "0"}>
        ${rebootPlanPreview.ok
          ? ("Will arm · " + rebootPlanPreview.factWhen)
          : (rebootPlanPreview.error || "Pick a valid schedule")}
      </p>
      <ul className="mh-reboot__notes">
        <li>Link drops for the reboot window · presence returns after boot.</li>
        <li>Not a factory reset · skills, config, and SSID stay on the board.</li>
        <li data-tone="warn">SSID is reported read-only here — change Wi-Fi under Hardware · SSID.</li>
        ${rebootMode === "weekly" ? html`<li data-tone="warn">Weekly = next occurrence via at_ts · not a forever cron.</li>` : null}
        ${local ? html`<li data-tone="warn">Localhost · Live schedule may be unavailable here.</li>` : null}
      </ul>
      <div className="mh-reboot__act">
        <button type="button" className="mh-reboot__go" disabled=${act.busy || !rebootPlanPreview.ok || !cmdOk}
          onClick=${scheduleReboot}>
          <${Ico} d="refresh" w=${13}/>
          ${act.busy ? "Arming…" : (rebootPlanPreview.ok ? ("Schedule · " + rebootPlanPreview.whenSay) : "Fix schedule")}
        </button>
        <button type="button" className="mh-reboot__cancel"
          onClick=${() => { setRebootOpen(false); setOpenLane((v) => v === "reboot" ? "" : v); }}>Cancel</button>
        ${rebootStatus && rebootStatus.say
          ? html`<p className="mh-reboot__status" data-tone=${rebootStatus.tone}>${rebootStatus.say}</p>`
          : html`<p className="mh-reboot__status" data-tone="idle">Confirm gate opens next · ack required</p>`}
      </div>
    </div>`;

  const rebootPanel = html`
    <div className="mh-reboot" data-open=${rebootOpen ? "1" : "0"}
      data-tone=${(rebootStatus && rebootStatus.tone) || "idle"}
      data-band="reboot">
      <button type="button" className="mh-reboot__sum" aria-expanded=${rebootOpen ? "true" : "false"}
        aria-controls=${"mh-reboot-body-" + did}
        onClick=${() => setRebootOpen((v) => !v)}>
        <div className="mh-loc__main">
          <span className="mh-editrow__k">Schedule reboot</span>
          <b className="mh-editrow__v">${rebootSummary}</b>
          <em className="mh-loc__edit" aria-hidden="true">${rebootOpen ? "▾" : "▸"} arm</em>
        </div>
      </button>
      ${rebootOpen ? rebootBody : null}
      ${!rebootOpen && rebootStatus && rebootStatus.say ? html`
        <p className="mh-reboot__status mh-reboot__status--foot" data-tone=${rebootStatus.tone}>${rebootStatus.say}</p>` : null}
    </div>`;

  const showNet = facet !== "board";
  const showBoard = facet !== "net";
  const draftMark = local && soft.ip && soft.ip !== liveIp;
  const fused = facet === "board" || flat === true;

  const powerBlock = showBoard ? (fused ? html`
    <div className="mh-ops__plane" data-band="power" aria-label="Power · thermal">
      <${FactRow} keyFact label="PoE power" value=${fmtWatts(poeWatts)}/>
      <${FactRow} label="PoE Vin"
        value=${poeVin != null && Number.isFinite(poeVin) ? (poeVin.toFixed(1) + " V") : "—"}/>
      <${FactRow} keyFact label="Chip temp" value=${fmtTemp(chipTemp)}/>
    </div>` : html`
    <div className="mh-edit" data-open=${openLane === "power" ? "1" : "0"}>
      <button type="button" className="mh-edit__head" aria-expanded=${openLane === "power" ? "true" : "false"}
        onClick=${() => setOpenLane((v) => v === "power" ? "" : "power")}>
        <span><small>Power · thermal</small><b>${fmtWatts(poeWatts)} · ${fmtTemp(chipTemp)}</b></span>
        <i>${openLane === "power" ? "▾" : "▸"}</i>
      </button>
      ${openLane === "power" ? html`
        <div className="mh-edit__body">
          <div className="mh-ops__facts">
            <div className="mux__field"><u>PoE power</u><b>${fmtWatts(poeWatts)}</b></div>
            <div className="mux__field"><u>PoE Vin</u><b>${poeVin != null && Number.isFinite(poeVin) ? (poeVin.toFixed(1) + " V") : "—"}</b></div>
            <div className="mux__field"><u>Chip temp</u><b>${fmtTemp(chipTemp)}</b></div>
          </div>
        </div>` : null}
    </div>`) : null;

  const gpioBlock = showBoard ? (fused ? html`
    <div className="mh-ops__plane" data-band="gpio" aria-label="GPIO seats">
      <${FactRow} keyFact label="GPIO seats"
        value=${gpioRows.length
          ? (gpioRows.filter((g) => g.used).length + " used · " + gpioRows.length + " seats")
          : "No pin map"}
        hint=${gpioRows.length ? "" : "No pin map reported · seats stay empty"}/>
      ${gpioRows.length ? html`
        <div className="mh-ops__gpio mh-ops__gpio--plane" role="list">
          ${gpioRows.map((g) => html`
            <div key=${g.key} className="mh-ops__gpio-row" role="listitem" data-used=${g.used ? "1" : "0"}>
              <b>${g.label}</b>
              <code>g${g.gpio}</code>
              <em>${g.role}</em>
              <span>${g.wake}</span>
            </div>`)}
        </div>` : null}
    </div>` : html`
    <div className="mh-edit" data-open=${openLane === "gpio" ? "1" : "0"}>
      <button type="button" className="mh-edit__head" aria-expanded=${openLane === "gpio" ? "true" : "false"}
        onClick=${() => setOpenLane((v) => v === "gpio" ? "" : "gpio")}>
        <span><small>GPIO seats</small>
          <b>${gpioRows.length ? (gpioRows.filter((g) => g.used).length + " used · " + gpioRows.length + " seats") : "No pin map"}</b></span>
        <i>${openLane === "gpio" ? "▾" : "▸"}</i>
      </button>
      ${openLane === "gpio" ? html`
        <div className="mh-edit__body">
          ${!gpioRows.length
            ? html`<p className="mh-ops__note">No pin map reported · seats stay empty.</p>`
            : html`<div className="mh-ops__gpio" role="list">
                ${gpioRows.map((g) => html`
                  <div key=${g.key} className="mh-ops__gpio-row" role="listitem" data-used=${g.used ? "1" : "0"}>
                    <b>${g.label}</b>
                    <code>g${g.gpio}</code>
                    <em>${g.role}</em>
                    <span>${g.wake}</span>
                  </div>`)}
              </div>`}
        </div>` : null}
    </div>`) : null;

  const rebootBlock = showBoard ? (fused ? html`
    <div className="mh-ops__plane" data-band="reboot" aria-label="Schedule reboot">
      ${rebootPanel}
    </div>` : html`
    <div className="mh-edit mh-edit--act" data-open=${openLane === "reboot" ? "1" : "0"}>
      <button type="button" className="mh-edit__head" aria-expanded=${openLane === "reboot" ? "true" : "false"}
        onClick=${() => {
          setOpenLane((v) => {
            const next = v === "reboot" ? "" : "reboot";
            setRebootOpen(next === "reboot");
            return next;
          });
        }}>
        <span><small>Schedule reboot</small><b>${rebootSummary}</b></span>
        <i>${openLane === "reboot" ? "▾" : "▸"}</i>
      </button>
      ${openLane === "reboot" ? html`
        <div className="mh-edit__body mh-edit__body--reboot">
          <div className="mh-reboot" data-open="1" data-tone=${(rebootStatus && rebootStatus.tone) || "idle"}>
            ${rebootBody}
          </div>
        </div>` : null}
    </div>`) : null;

  return html`
    <section className=${"mh-ops" + (fused ? " mh-ops--fuse mh-ops--plane" : "")}
      aria-label="Board ops" data-facet=${facet} data-flat=${fused ? "1" : "0"} data-plane=${fused ? "1" : "0"}>
      ${act.envelope
        ? html`<p className="note" data-cmc-last-action="1">Last action · ${envelopeSummary(act.envelope) || "—"}</p>`
        : null}
      ${showNet ? html`
      <div className="mh-edit" data-open=${openLane === "lan" ? "1" : "0"}>
        <button type="button" className="mh-edit__head" aria-expanded=${openLane === "lan" ? "true" : "false"}
          onClick=${() => setOpenLane((v) => v === "lan" ? "" : "lan")}>
          <span>
            <small>Network · LAN${draftMark ? " · draft" : ""}</small>
            <b>${liveIp || ip || "No IP reported"}</b>
          </span>
          <i>${openLane === "lan" ? "▾" : "▸"}</i>
        </button>
        ${openLane === "lan" ? html`
        <div className="mh-edit__body">
          <div className="mh-ops__facts mh-edit__reported">
            <div className="mux__field"><u>Reported IP</u><b>${liveIp || "—"}</b></div>
            <div className="mux__field"><u>Reported gateway</u><b>${liveGw || "—"}</b></div>
            <div className="mux__field"><u>Reported subnet</u><b>${liveMask || "—"}</b></div>
          </div>
          <p className="mh-ops__note">${local
            ? "Edit draft below · Save validates IPv4 · Live push plane not armed yet."
            : "LAN edits disabled · Live net push plane not armed on this host."}</p>
          <div className="machine-v2__identity mh-ops__edit mh-edit__fields">
            <label className="mux__field mux__field--edit"><u>Board IP</u>
              <input className="inp" value=${ip} placeholder="e.g. 192.168.1.50" inputMode="decimal"
                onChange=${(e) => { setIp(e.target.value); setNetDirty(true); }}/></label>
            <label className="mux__field mux__field--edit"><u>Gateway</u>
              <input className="inp" value=${gateway} placeholder="e.g. 192.168.1.1" inputMode="decimal"
                onChange=${(e) => { setGateway(e.target.value); setNetDirty(true); }}/></label>
            <label className="mux__field mux__field--edit"><u>Subnet mask</u>
              <input className="inp" value=${subnet} placeholder="e.g. 255.255.255.0" inputMode="decimal"
                onChange=${(e) => { setSubnet(e.target.value); setNetDirty(true); }}/></label>
          </div>
          <div className="mux__panel-acts">
            <button type="button" className="btn btn--go btn--sm" disabled=${!netDirty || act.busy || !local}
              onClick=${saveNetDraft}>${act.busy ? "Saving…" : (local ? "Save LAN draft" : "Save refused")}</button>
          </div>
        </div>` : null}
      </div>

      <div className="mh-edit" data-open=${openLane === "wifi" ? "1" : "0"}>
        <button type="button" className="mh-edit__head" aria-expanded=${openLane === "wifi" ? "true" : "false"}
          onClick=${() => setOpenLane((v) => v === "wifi" ? "" : "wifi")}>
          <span>
            <small>Wi‑Fi · SSID</small>
            <b>${(!local && wifiOpsView && wifiOpsView.liveSsid) ? wifiOpsView.liveSsid : (ssid || "—")}</b>
          </span>
          <i>${openLane === "wifi" ? "▾" : "▸"}</i>
        </button>
        ${openLane === "wifi" ? html`
        <div className="mh-edit__body">
          <p className="mh-ops__note">
            ${local
              ? (ssid
                ? "Password Save · local until Live Wi‑Fi push is armed."
                : "SSID empty · join Wi‑Fi on board first. With Wi‑Fi · LAN, scan from the unit Hardware plane.")
              : (wifiOpsView && wifiOpsView.inflight
                ? "Live job in flight · waiting for device ACK + telemetry."
                : (wifiOpsView && wifiOpsView.switchConfirmed
                  ? "Live link confirmed by device telemetry."
                  : "Live wifi-ops · switch requires passphrase + device ACK."))}
          </p>
          ${ssid ? html`
          <div className="machine-v2__identity mh-ops__edit mh-edit__fields">
            <label className="mux__field mux__field--edit"><u>SSID</u>
              <input className="inp" value=${ssid} readOnly maxLength=${32}/></label>
            <label className="mux__field mux__field--edit"><u>Passphrase</u>
              <input className="inp" type="password" value=${psk} placeholder="New passphrase · then Save"
                onChange=${(e) => { setPsk(e.target.value); setWifiDirty(true); }}/></label>
          </div>
          <div className="mux__panel-acts">
            <button type="button" className="btn btn--go btn--sm"
              disabled=${!wifiDirty || act.busy || !String(psk || "").trim() || (!local && !cmdOk)}
              onClick=${queueWifi}>
              ${local ? "Save Wi‑Fi locally" : "Switch Wi-Fi"}
            </button>
          </div>` : null}
        </div>` : null}
      </div>` : null}

      ${powerBlock}
      ${gpioBlock}
      ${rebootBlock}
    </section>`;
}
